import React, { useEffect, useState } from 'react'
import { Alert, Card, Badge, Spinner } from 'react-bootstrap'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'

interface RolePermissionsDisplayProps {
  roleId: string
  isSystemRole: boolean
}

interface PermissionGroup {
  category: string
  permissions: string[]
}

export const RolePermissionsDisplay: React.FC<RolePermissionsDisplayProps> = ({
  roleId,
  isSystemRole
}) => {
  const httpClient = useAxiosHttpClient()
  const [permissions, setPermissions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadPermissions()
  }, [roleId, isSystemRole])

  const loadPermissions = async () => {
    if (!roleId) {
      setPermissions([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      console.log(
        '[RolePermissionsDisplay] Loading permissions for role:',
        roleId,
        'isSystemRole:',
        isSystemRole
      )
      const response = await httpClient.get?.({
        url: `${URL_API}/permissions/role/${roleId}?isSystemRole=${isSystemRole}`
      })

      console.log('[RolePermissionsDisplay] Permissions response:', response)

      // Response is an array of permission strings
      if (Array.isArray(response)) {
        console.log(
          '[RolePermissionsDisplay] Setting permissions from array:',
          response
        )
        setPermissions(response)
      } else if (response && Array.isArray(response.permissions)) {
        console.log(
          '[RolePermissionsDisplay] Setting permissions from response.permissions:',
          response.permissions
        )
        setPermissions(response.permissions)
      } else {
        console.log('[RolePermissionsDisplay] No permissions found in response')
        setPermissions([])
      }
    } catch (err: any) {
      console.error('[RolePermissionsDisplay] Error loading permissions:', err)
      setError('Não foi possível carregar as permissões')
    } finally {
      setLoading(false)
    }
  }

  const groupPermissionsByCategory = (): PermissionGroup[] => {
    const grouped: { [key: string]: string[] } = {}

    permissions.forEach((permission) => {
      const [category] = permission.split('.')
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(permission)
    })

    return Object.entries(grouped).map(([category, perms]) => ({
      category,
      permissions: perms
    }))
  }

  const getCategoryDisplayName = (category: string): string => {
    const names: { [key: string]: string } = {
      companies: 'Empresas',
      spes: 'SPEs',
      projects: 'Empreendimentos',
      units: 'Unidades',
      contracts: 'Contratos',
      payments: 'Pagamentos',
      billings: 'Pagamentos',
      users: 'Usuários',
      roles: 'Funções',
      permissions: 'Permissões',
      reports: 'Relatórios',
      documents: 'Documentos',
      blockchain: 'Blockchain',
      receivables: 'Recebíveis',
      standalone_billings: 'Cobranças Avulsas',
      bank_statement_entries: 'Conciliação do Extrato'
    }
    return names[category] || category
  }

  const getPermissionDisplayName = (permission: string): string => {
    const [category, action] = permission.split('.')
    const actions: { [key: string]: string } = {
      read: 'Visualizar',
      create: 'Criar',
      update: 'Editar',
      delete: 'Excluir',
      manage: 'Gerenciar',
      export: 'Exportar',
      export_report: 'Exportar Relatório'
    }
    return actions[action] || action
  }

  if (!roleId) {
    return null
  }

  if (loading) {
    return (
      <Card className="mt-3">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" className="mr-2" />
          <span>Carregando permissões...</span>
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Alert variant="warning" className="mt-3">
        {error}
      </Alert>
    )
  }

  if (permissions.length === 0) {
    return (
      <Alert variant="info" className="mt-3">
        <i className="feather icon-info mr-2" />
        Esta função não possui permissões definidas
      </Alert>
    )
  }

  const groupedPermissions = groupPermissionsByCategory()

  return (
    <Card className="mt-3">
      <Card.Header>
        <strong>
          <i className="feather icon-shield mr-2" />
          Permissões desta Função
        </strong>
      </Card.Header>
      <Card.Body>
        <div className="row">
          {groupedPermissions.map((group) => (
            <div key={group.category} className="col-md-6 mb-3">
              <h6 className="text-muted mb-2">
                <i className="feather icon-layers mr-1" />
                {getCategoryDisplayName(group.category)}
              </h6>
              <div>
                {group.permissions.map((permission) => (
                  <Badge
                    key={permission}
                    variant="primary"
                    className="mr-2 mb-2"
                    style={{ fontSize: '0.85rem', fontWeight: 'normal' }}
                  >
                    <i className="feather icon-check mr-1" />
                    {getPermissionDisplayName(permission)}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-top">
          <small className="text-muted">
            <i className="feather icon-info mr-1" />
            Total de {permissions.length} permissão(ões)
          </small>
        </div>
      </Card.Body>
    </Card>
  )
}
